import { regexForNumberGreaterThanOrEqualTo, regexForNumberLessThanOrEqualTo } from "./regexUtils.js";
import { DeviceVersion } from "../schemas/commonSubSchemas/deviceVersion.js";
import { deviceVersionStringToObject } from "./VerdiDeviceVersionUtilities.js";

/**
 * Regex pattern generators for version selector strings.
 * Used where only regex-based validation is available (e.g. Zod schema constraints).
 *
 * For runtime version matching, use `doesDeviceVersionMatchSelector` in VerdiDeviceVersionUtilities.
 *
 * `doesDeviceVersionMatchVersionSelectorBasedOnRegex` is exported for **test infrastructure only**:
 * it exercises the regex factory against the canonical matcher so we can catch regressions in the
 * generated patterns. Do not use it in production code.
 *
 * Note: The regex-based matcher cannot detect malformed version selectors. If the version selector
 * is malformed, the output is considered undefined.
 */

/**
 * Type guard to check if a version parameter is a string.
 *
 * @param version - A DeviceVersion object or version string
 * @returns True if version is a string, with type narrowing to string
 */
function isDeviceVersionString(version: DeviceVersion | string): version is string {
    return typeof version === "string" || version instanceof String;
}
/**
 * Generates a regex pattern that matches a single numeric version selector.
 * Supports wildcards (*), exact matches, ranges (min:max), greater-than (min+), and less-than (max-).
 *
 * @param number - The numeric value to match against
 * @returns A regex pattern string matching various numeric selector formats
 *
 * @example
 * // Matches: "4", "*", "3+", "5-", "2:6"
 * getRegexToMatchSingleNumericSelector(4)
 */
function getRegexToMatchSingleNumericSelector(number: number): string {
    const options = [
        "(?:\\*)",
        `(?:${number})`,
        `(?:${regexForNumberLessThanOrEqualTo(number)}\\+)`,
        `(?:${regexForNumberGreaterThanOrEqualTo(number)}\\-)`,
        `(?:${regexForNumberLessThanOrEqualTo(number)}\\:${regexForNumberGreaterThanOrEqualTo(number)})`,
    ];
    return `(?:${options.join("|")})`;
}

/**
 * Generates a regex pattern that matches a numeric term in a version selector.
 * Handles both single values and comma-separated lists in brackets.
 *
 * @param number - The numeric value to match against
 * @returns A regex pattern string matching numeric terms like "[2,4,6]" or "3+" or "5"
 *
 * @example
 * // Matches: "4", "[2,4,6]", "[1:5, 8+]"
 * getRegexToMatchSingleNumericTerm(4)
 */
function getRegexToMatchSingleNumericTerm(number: number): string {
    return `(\\[*([\\d*\\-\\+\\: ]*,)* *${getRegexToMatchSingleNumericSelector(number)} *,* *([\\d*\\-\\+\\: ]*,*)*\\]*)`;
}

/**
 * Generates a regex pattern that matches a non-numeric term (like product line or hardware variant).
 * Handles both single values and comma-separated lists in brackets.
 *
 * @param name - The string value to match against
 * @returns A regex pattern string matching terms like "[mega,micro]" or "mega" or "*"
 *
 * @example
 * // Matches: "mega", "[mega,micro]", "*"
 * getRegexToMatchSingleNonNumericTerm("mega")
 */
function getRegexToMatchSingleNonNumericTerm(name: string): string {
    return `(\\[*([a-zA-Z0-9, ]*,)* *(?:${name}|\\*) *,* *([a-zA-Z0-9, ]*,*)*\\]*)`;
}

/**
 * Generates a regex pattern that matches a complete single version selector from a DeviceVersion object.
 * Combines all version components (generation, productLine, hardwareVersion, hardwareVariant, firmwareVersion).
 *
 * @param version - The DeviceVersion object to generate a matcher for
 * @returns A regex pattern string matching the complete version selector
 *
 * @example
 * // For version {generation: 3, productLine: "mega", hardwareVersion: 2, hardwareVariant: "a", firmwareVersion: 5}
 * // Matches: "3.mega.2.a.5", "3.[mega,micro].2+.a.5", etc.
 * getRegexToMatchSingleVersionSelectorFromVersion(deviceVersion)
 */
function getRegexToMatchSingleVersionSelectorFromVersion(version: DeviceVersion): string {
    const terms = [
        getRegexToMatchSingleNumericTerm(version.generation),
        getRegexToMatchSingleNonNumericTerm(version.productLine),
        getRegexToMatchSingleNumericTerm(version.hardwareVersion),
        getRegexToMatchSingleNonNumericTerm(version.hardwareVariant),
        getRegexToMatchSingleNumericTerm(version.firmwareVersion),
    ];
    return `(?:${terms.join("\\.")})`;
}

/**
 * Generates a regex pattern that matches a version selector (with or without parentheses).
 * This is used to match positive version constraints in a selector string.
 *
 * Careful! You must use both the positive and negative selectors for this to work properly.
 * And negative selectors must override positive ones. Otherwise your results will be wrong. Example
 * implementations are below this function
 *
 * @param version - The DeviceVersion object to generate a matcher for
 * @returns A regex pattern string matching version selectors like "3.mega.2.a.5" or "(3.mega.2.a.5)"
 *
 *
 *
 * @example
 * // Matches: "3.mega.2.a.5" or "(3.mega.2.a.5)"
 * getRegexToMatchVersionSelectorFromVersion(deviceVersion)
 */
export function getRegexToMatchPositiveVersionSelectorFromVersion(version: DeviceVersion): string {
    return `(?: *${getRegexToMatchSingleVersionSelectorFromVersion(version)} *)|(?: *\\( *${getRegexToMatchSingleVersionSelectorFromVersion(version)} *\\) *)`;
}

/**
 * Generates a regex pattern that matches a negative version selector (exclusion constraint).
 * This is used to match "!(version)" patterns that exclude specific versions.
 *
 * Careful! You must use both the positive and negative selectors for this to work properly.
 * And negative selectors must override positive ones. Otherwise your results will be wrong. Example
 * implementations are below this function
 *
 * @param version - The DeviceVersion object to generate a matcher for
 * @returns A regex pattern string matching negative selectors like "!(3.mega.2.a.5)"
 *
 * @example
 * // Matches: "!(3.mega.2.a.5)"
 * getRegexToMatchNegativeVersionSelectorFromVersion(deviceVersion)
 */
export function getRegexToMatchNegativeVersionSelectorFromVersion(version: DeviceVersion): string {
    return `\\!\\( *${getRegexToMatchSingleVersionSelectorFromVersion(version)} *\\)`;
}

/**
 * **Test infrastructure only.** Do not call from production code.
 *
 * Exercises the regex pattern generators above by matching a device version against a selector
 * string the same way the Zod schema constraints would. Cross-tested in
 * `verdiDeviceVersionMatching.test.ts` against `doesDeviceVersionMatchSelector` to ensure the
 * regex factory stays robust.
 *
 * @param version - A DeviceVersion object or version string to test
 * @param versionSelector - The version selector string to test against (e.g., "(3.mega.2+.a.5) !(3.mega.2.a.4)")
 * @returns True if the version matches the selector (included and not excluded), false otherwise
 *
 * @example
 * // Returns true if version "3.mega.2.a.5" matches selector "(3.mega.2+.a.5)"
 * doesDeviceVersionMatchVersionSelectorBasedOnRegex("3.mega.2.a.5", "(3.mega.2+.a.5)")
 */
export function doesDeviceVersionMatchVersionSelectorBasedOnRegex(
    version: DeviceVersion | string,
    versionSelector: string,
): boolean {
    const deviceVersion: DeviceVersion = isDeviceVersionString(version)
        ? deviceVersionStringToObject(version)
        : version;

    return (
        new RegExp(`${getRegexToMatchPositiveVersionSelectorFromVersion(deviceVersion)}`, "i").test(versionSelector) &&
        !new RegExp(`${getRegexToMatchNegativeVersionSelectorFromVersion(deviceVersion)}`, "i").test(versionSelector)
    );
}
